<template>
  <!-- State: No Spaces | No Results | Error -->
  <div class="h-full flex justify-center" v-show="state">
    <slot name="state" />
  </div>

  <!-- Data: GroupBuilder -->
  <div v-if="isLoading">
    <GroupBuilderItemSkeleton v-for="i in 15" :key="i" />
  </div>

  <GroupBuilder
    v-show="!state"
    v-if="!isLoading"
    :key="spaces"
    :data="spaces"
    :isEditMode="isEditMode"
    :isDeleteMode="isDeleteMode"
    :isAddMode="isAddMode"
    :query="query"
    @show:detail="$emit('show:detail', $event)"
    ref="groupBuilderRef"
    type="spaces"
    @update:count="emit('update:count', $event)"
    @move:item="handleMoveSpace"
  >
    <template #default="{ data }">
      <div
        class="flex flex-col z-10"
        :class="
          isEmpty(data.item.value.children) ||
          isEditMode === data.item.value.id ||
          isDeleteMode === data.item.value.id
            ? 'w-full'
            : 'w-[calc(100%-22px)] md:w-[calc(100%-2rem)]'
        "
      >
        <div class="flex relative w-full cursor-pointer items-center truncate">
          <div
            v-if="isEditMode === data.item.value.id"
            class="flex grow gap-4 pr-2 py-0.5"
            @click.stop
          >
            <div class="w-full">
              <TextInput
                ref="inputRef"
                @update:modelValue="name = $event"
                :modelValue="data.item.value.name"
                @keydown.stop
                @keyup.enter="
                  handleUpdateSpace({
                    name: name,
                    identifier: data.item.value.identifier,
                    id: data.item.value.id,
                  })
                "
                size="sm"
              />
            </div>
            <div class="flex gap-1.5">
              <Button
                size="sm"
                :label="$t('components.buttons.cancel')"
                :disabled="isUpdating"
                variant="secondary"
                type="gray"
                @click="handleEditMode(data.item.value)"
              />
              <Button
                size="sm"
                :loading="isUpdating"
                :label="$t('components.buttons.save')"
                variant="primary"
                @click="
                  handleUpdateSpace({
                    name: name,
                    identifier: data.item.value.identifier,
                    id: data.item.value.id,
                  })
                "
              />
            </div>
          </div>
          <div
            v-else
            class="flex grow md:gap-2 justify-start items-start md:items-center md:justify-between md:flex-row flex-col truncate"
            :class="{
              'md:pl-2': isEmpty(data.item.value.children),
            }"
          >
            <div
              class="w-full flex gap-2 items-center md:py-2 truncate"
              :class="{
                'items-center': !isEmpty(data.item.value.children),
                'md:my-0 my-1':
                  !isEmpty(getBadges(data)) &&
                  !isEmpty(data.item.value.children),
              }"
            >
              <Indicator
                size="xs"
                :variant="'space'"
                :imageUrl="data.item.value.thumbnailUrl"
              />
              <div class="truncate">
                <span
                  class="truncate"
                  v-for="(part, index) in highlightedParts(
                    data.item.value.name
                  )"
                  :key="index"
                >
                  <mark v-if="part.highlight" class="bg-yellow-300">{{
                    part.text
                  }}</mark>
                  <template v-else>{{ part.text }}</template>
                </span>
              </div>
              <div
                v-if="data.item.value.identifier"
                class="truncate"
                :class="{
                  'max-w-40':
                    highlightedParts(data.item.value.identifier).length < 2,
                }"
              >
                <Badge type="id">
                  <template #label>
                    <span
                      class="truncate"
                      v-for="(part, index) in highlightedParts(
                        data.item.value.identifier
                      )"
                      :key="index"
                    >
                      <mark v-if="part.highlight" class="bg-yellow-300">{{
                        part.text
                      }}</mark>
                      <template v-else>{{ part.text }}</template>
                    </span>
                  </template>
                </Badge>
              </div>
            </div>
            <div
              v-if="isAddMode === data.item.value.id"
              class="flex items-center relative pr-2"
              @click.stop
            >
              <div class="flex gap-1.5">
                <Button
                  size="sm"
                  :label="$t('components.buttons.cancel')"
                  :disabled="isAdding"
                  variant="secondary"
                  type="gray"
                  @click="handleAddMode(data.item.value)"
                />
                <Button
                  size="sm"
                  :label="$t('components.buttons.save')"
                  :loading="isAdding"
                  :disabled="!(isDirty || !isEmpty(names))"
                  variant="primary"
                  @click="
                    handleAddSpace(data.item.value ? data.item.value.id : null)
                  "
                />
              </div>
            </div>
            <div
              v-else-if="isDeleteMode === data.item.value.id"
              class="flex grow gap-1.5 pr-2 py-0.5 justify-end"
              @click.stop
            >
              <Button
                size="sm"
                :label="$t('components.buttons.cancel')"
                :disabled="isDeleting"
                variant="secondary"
                type="gray"
                @click.stop="handleDeleteMode(data.item.value)"
              />
              <Button
                size="sm"
                :loading="isDeleting"
                :label="$t('components.buttons.delete')"
                variant="primary"
                type="destructive"
                @click.stop="handleDeleteSpace(data.item.value.id)"
              />
            </div>
            <div v-else class="flex z-10 items-center relative pr-2">
              <div
                class="flex gap-1 md:group-hover:hidden overflow-hidden"
                :class="{
                  'pt-2 md:pt-0': !isEmpty(getBadges(data)),
                }"
              >
                <Badge
                  v-for="(badge, index) in getBadges(data)"
                  :key="index"
                  :label="badge.count"
                  type="color"
                  size="sm"
                  icon="leading"
                >
                  <Icon
                    :name="badge.icon"
                    variant="fill"
                    :class="badge.class"
                  />
                </Badge>
              </div>
              <div
                @click.stop
                v-if="!maxSmall"
                class="hidden items-center gap-1 z-10 group-hover:flex"
              >
                <Tooltip
                  :title="$t('components.tooltips.add_spaces')"
                  :disabled="!canCreateSpaces"
                  side="bottom"
                >
                  <IconButton
                    @click.stop="handleAddMode(data.item.value)"
                    size="xs"
                    variant="secondary"
                    type="gray"
                    :disabled="!canCreateSpaces"
                  >
                    <Icon name="plus" />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  :title="$t('components.tooltips.edit')"
                  :disabled="!canUpdateSpaces"
                  side="bottom"
                >
                  <IconButton
                    @click.stop="handleEditMode(data.item.value)"
                    size="xs"
                    variant="secondary"
                    type="gray"
                    :disabled="!canUpdateSpaces"
                  >
                    <Icon name="edit-pen" />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  :title="$t('components.tooltips.duplicate')"
                  side="bottom"
                  :disabled="!canCreateSpaces"
                >
                  <IconButton
                    @click.stop="handleDuplicateSpace(data.item.value.id)"
                    :disabled="isDuplicating || !canCreateSpaces"
                    size="xs"
                    variant="secondary"
                    type="gray"
                  >
                    <Icon name="copy" />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  :title="$t('components.tooltips.delete')"
                  side="bottom"
                  :disabled="!canDeleteSpaces"
                >
                  <IconButton
                    @click.stop="handleDeleteMode(data.item.value)"
                    size="xs"
                    icon
                    variant="secondary"
                    type="gray"
                    :disabled="!canDeleteSpaces"
                  >
                    <Icon name="delete" />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  :title="$t('components.tooltips.drag')"
                  side="bottom"
                  :disabled="!canUpdateSpaces"
                >
                  <IconButton
                    size="xs"
                    variant="tertiary"
                    type="gray"
                    :class="{
                      'pointer-events-none': !canUpdateSpaces,
                    }"
                    :disabled="!canUpdateSpaces"
                  >
                    <DragHandle />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          </div>
          <IconButton
            v-if="
              maxSmall &&
              (canCreateSpaces || canUpdateSpaces || canDeleteSpaces)
            "
            size="xs"
            variant="tertiary"
            type="gray"
            @click.stop="handleOpenActionsDrawer(data)"
          >
            <Icon name="dots-vertical" variant="fill" />
          </IconButton>
        </div>
        <div
          class="pt-6 px-4 pb-4 flex flex-col gap-3 border-t mt-2 border-secondary -mr-2"
          v-if="isAddMode === data.item.value.id"
          @click.stop
          :class="
            isEmpty(data.item.value.children) ? '-ml-[8px]' : '-ml-[38px]'
          "
        >
          <div class="text-sm text-tertiary">
            {{ $t("components.spaces.create.description") }}
          </div>
          <GroupsInput
            @update:isDirty="isDirty = $event"
            @keydown.stop
            as="textarea"
            :placeholder="$t('components.spaces.create.form.name.placeholder')"
            variant="space"
            v-model="names"
          />
        </div>
      </div>
    </template>
  </GroupBuilder>
  <Actions
    class="absolute"
    :item="item"
    :open="item"
    @delete:space="handleDeleteSpace"
    @duplicate:space="handleDuplicateSpace"
    @update:space="handleUpdateSpace"
    @update:open="handleOpenActionsDrawer"
  />
</template>

<script setup>
import { ref, inject, computed, watch, onMounted, nextTick } from "vue";
import { storeToRefs } from "pinia";
import route from "ziggy-js";
import axios from "axios";
import { isEmpty } from "lodash";
import { Tooltip } from "@/SharedComponents/Tooltips";
import { Actions } from "@/Pages/Spaces/Partials/Drawers";

import {
  GroupBuilder,
  GroupBuilderItemSkeleton,
} from "@/SharedComponents/GroupBuilder";
import { Button, IconButton } from "@/SharedComponents/Buttons";
import { Icon } from "@/SharedComponents/Icon";
import { TextInput } from "@/SharedComponents/Inputs";
import { Badge } from "@/SharedComponents/Badges";
import { GroupsInput } from "@/SharedComponents/Inputs";
import { Indicator } from "@/SharedComponents/Indicator";
import { DragHandle } from "@/SharedComponents/GroupBuilder";

import { useSpaces } from "@/Composables/useSpaces";
import { useFilters } from "@/Composables/useFilters";
import { useSites } from "@/Composables/useSites";
import { useBreakpoints } from "@/Composables/useBreakpoints";

const { hasPermission, permissions } = inject("permissions");

const spaces = ref([]);
const totalSpaces = ref(0);
const isEditMode = ref(undefined);
const isDeleteMode = ref(undefined);
const isAddMode = ref(undefined);
const item = ref(null);
const name = ref("");
const names = ref([]);
const isDirty = ref(false);

const isLoading = ref(false);
const isUpdating = ref(false);
const isDeleting = ref(false);
const isAdding = ref(false);
const isDuplicating = ref(false);

const inputRef = ref(null);
const groupBuilderRef = ref(null);

const globalFilterStore = useFilters("global");
const { filterValues: globalFilterValues } = storeToRefs(globalFilterStore);

const { deleteSpace, updateSpace, addSpace, moveSpace, duplicateSpace } =
  useSpaces();

// Emitter
const emitter = inject("emitter");

// Emits
const emit = defineEmits(["update:count", "update:fetchingStatus"]);

// Buildings
const { isLoading: isLoadingBuildings, data: buildingsData } = useSites();

// Define Props
const props = defineProps({
  query: { type: String, default: "" },
  count: { type: Number, default: 0 },
  fetchingStatus: { type: String, required: true },
});

// Breakpoints
const { maxSmall } = useBreakpoints();

// Computed Props
const state = computed(() => {
  if (props.fetchingStatus === "loading") {
    return false;
  }

  return props.count === 0 || props.fetchingStatus === "error";
});

const getBadges = (data) =>
  [
    {
      count: data.item.value.assets_count,
      icon: "asset",
      class: "!text-utility-orange-500",
    },
    {
      count: data.item.value.files_count,
      icon: "documents",
      class: "!text-utility-success-500",
    },
    {
      count: data.item.value.tasks_count,
      icon: "board-tasks",
      class: "!fg-brand-primary",
    },
    {
      count: data.item.value.qr_codes_count,
      icon: "qr-code",
      class: "!text-quaternary",
    },
  ].filter((badge) => badge.count > 0);

const buildingId = computed(() => globalFilterValues.value.tenant?.[0] || "");

const buildings = computed(() => {
  if (isLoadingBuildings.value) {
    return [];
  }

  return buildingsData.value.filter((building) =>
    hasPermission(permissions.createSpace, building.id)
  );
});

const canCreateSpaces = computed(() => {
  if (!buildingId.value) {
    return !isEmpty(buildings.value);
  }

  return hasPermission(permissions.createSpace, buildingId.value);
});

const canUpdateSpaces = computed(() => {
  if (!buildingId.value) {
    return !isEmpty(buildings.value);
  }

  return hasPermission(permissions.updateSpace, buildingId.value);
});

const canDeleteSpaces = computed(() => {
  if (!buildingId.value) {
    return !isEmpty(buildings.value);
  }

  return hasPermission(permissions.deleteSpace, buildingId.value);
});

// Emitters
emitter.on("reload-spaces", () => {
  getSpaces(false);
});

watch(
  buildingId,
  () => {
    getSpaces(true);
  },
  {
    deep: true,
  }
);

watch(
  () => props.query,
  () => {
    searchTree(spaces.value, props.query);
  }
);

const searchTree = (tree, searchTerm) => {
  if (!searchTerm) return tree;

  const lowerTerm = searchTerm.toLowerCase();

  return tree
    .map((node) => {
      const matches = node.name.toLowerCase().includes(lowerTerm);
      const children = node.children
        ? searchTree(node.children, searchTerm)
        : [];
      if (matches || children.length) {
        return { ...node, matches, children };
      }
      return null;
    })
    .filter(Boolean);
};

const handleMoveSpace = (value) => {
  const { id, parentId } = value;

  moveSpace(id, parentId, () => {
    getSpaces(false);
  });
};

const handleEditMode = (item) => {
  isEditMode.value = isEditMode.value === item.id ? undefined : item.id;
  name.value = isEditMode.value ? item.name : "";

  if (isEditMode.value === item.id) {
    nextTick(() => {
      inputRef.value.focus();
    });
  }
};

const handleDeleteMode = (item) => {
  isDeleteMode.value = isDeleteMode.value === item.id ? undefined : item.id;
};

const handleAddMode = (item) => {
  isAddMode.value = isAddMode.value === item.id ? undefined : item.id;

  if (!isAddMode.value) {
    isDirty.value = false;
  }

  names.value = [];
};

const handleAddSpace = (parentGroupId) => {
  isAdding.value = true;

  addSpace(names.value, parentGroupId, () => {
    isAdding.value = false;
    isAddMode.value = undefined;
    isDirty.value = false;
    names.value = [];
    getSpaces(false);
  });
};

const handleDeleteSpace = (id) => {
  handleOpenActionsDrawer(false);

  isDeleting.value = true;

  deleteSpace(id, () => {
    isDeleting.value = false;
    isDeleteMode.value = undefined;
    getSpaces(false);
  });
};

const handleUpdateSpace = (data) => {
  handleOpenActionsDrawer(false);

  isUpdating.value = true;

  updateSpace(data.name, data.identifier, data.id, () => {
    isUpdating.value = false;
    isEditMode.value = undefined;
    name.value = "";
    getSpaces(false);
  });
};

const handleDuplicateSpace = (id) => {
  handleOpenActionsDrawer(false);

  isDuplicating.value = true;

  duplicateSpace(id, () => {
    isDuplicating.value = false;
    getSpaces(false);
  });
};

const handleOpenActionsDrawer = (data) => {
  item.value = data.item ? data.item.value : undefined;
};

const highlightedParts = (name) => {
  if (!props.query) return [{ text: name, highlight: false }];

  const parts = [];
  const lowerName = name.toLowerCase();
  const lowerQuery = props.query.toLowerCase();
  let lastIndex = 0;
  let index;

  while ((index = lowerName.indexOf(lowerQuery, lastIndex)) !== -1) {
    if (index > lastIndex) {
      parts.push({
        text: name.slice(lastIndex, index),
        highlight: false,
      });
    }
    parts.push({
      text: name.slice(index, index + props.query.length),
      highlight: true,
    });
    lastIndex = index + props.query.length;
  }

  if (lastIndex < name.length) {
    parts.push({ text: name.slice(lastIndex), highlight: false });
  }

  return parts;
};

// Get Data
async function getSpaces(showLoader) {
  if (showLoader) {
    isLoading.value = true;
    spaces.value = [];
  }

  emit("update:fetchingStatus", "loading");

  const parameters = {
    tenant_id: buildingId.value,
  };

  const url = route("api.spaces.search");

  await axios
    .post(url, {
      ...parameters,
    })
    .then((response) => {
      spaces.value = response.data.data;
      totalSpaces.value = response.data.data.length;
      emit("update:count", response.data.data.length);
      emit("update:fetchingStatus", "loaded");
    })
    .catch((err) => {
      console.log("error occurred", err);
      emit("update:count", 0);
      emit("update:fetchingStatus", "error");
    })
    .finally(() => {
      isLoading.value = false;
    });
}

// Lifecycle hooks
onMounted(() => {
  getSpaces(true);
});
</script>
